import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, CircularProgress, Avatar, Chip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import Controls from '../components/controls/Controls';
import * as MyCollections from "../services/MyCollections";
import * as CommonFunctions from '../services/CommonFunctions';
import { createInvoice } from '../components/CreateInvoice';
import useMediaQuery from '@mui/material/useMediaQuery';
import GenerateInvoiceDialog from '../components/GenerateInvoiceDialog';
import ConfirmPaymentDialog from '../components/ConfirmPaymentDialog';
import { useTheme } from '@mui/material/styles';
import { urlAccountingSettlement, urlAccountingStatements, urlSupervisors, urlAdditionalCosts } from '../config';
import { useAuthStore } from '../store/authStore';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import CustomTooltip from '../components/CustomTooltip';
import { useThemeContext } from '../components/contexts/ThemeContext';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))


const NameAvatarRenderer = (props) => {
  const accountantName = props.value;
  if (!accountantName || accountantName.trim() === '') {
    return <span style={{ color: props.context?.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>;
  }
  const parts = accountantName.split(' ');
  const surname = parts[0] || '';
  const name = parts.length > 1 ? parts[1] : '';
  const initials = `${surname.charAt(0)}${name.charAt(0)}`.toUpperCase();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context?.darkTheme ? '#10b981' : '#059669',
          color: '#ffffff',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: 'normal' }}>{accountantName}</span>
    </div>
  );
};

const StatementFinishedRenderer = (props) => {
  const isFinished = props.data.statementFinished;
  const isEdited = props.data.isEdited;
  const finishDate = props.data.finishDate;

  let label = "oczekuje...";
  let color = props.context?.darkTheme ? '#cbd5e1' : '#475569';
  let bg = props.context?.darkTheme ? '#1e293b' : '#f1f5f9';

  if (isFinished === true) {
    label = isEdited ? `${finishDate} (E)` : finishDate;
    color = props.context?.darkTheme ? '#6ee7b7' : '#065f46';
    bg = props.context?.darkTheme ? '#022c22' : '#d1fae5';
  } else if (isEdited) {
    label = "w edycji...";
    color = props.context?.darkTheme ? '#fcd34d' : '#92400e';
    bg = props.context?.darkTheme ? '#451a03' : '#fef3c7';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip label={label} size="small" sx={{ bgcolor: bg, color: color, fontWeight: 'normal', borderRadius: '6px', height: '24px' }} />
    </div>
  );
};

const GeneratedInvoicesRenderer = (props) => {
  const value = props.value;
  let color = props.context?.darkTheme ? '#cbd5e1' : '#475569';
  let bg = props.context?.darkTheme ? '#1e293b' : '#f1f5f9';

  if (value === 'wygenerowano') {
    color = props.context?.darkTheme ? '#6ee7b7' : '#065f46';
    bg = props.context?.darkTheme ? '#022c22' : '#d1fae5';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip label={value || "oczekuje..."} size="small" sx={{ bgcolor: bg, color: color, fontWeight: 'normal', borderRadius: '6px', height: '24px' }} />
    </div>
  );
};

const StatementApprovedRenderer = (props) => {
  const { statementApproved, approvalDate, finishDate } = props.data;
  let label = "oczekuje...";
  let color = props.context?.darkTheme ? '#cbd5e1' : '#475569';
  let bg = props.context?.darkTheme ? '#1e293b' : '#f1f5f9';

  if (statementApproved === true) {
    const daysDifference = CommonFunctions.calculateDaysDifference(finishDate, approvalDate);
    label = `zatwierdzone (${approvalDate}) (${daysDifference}D)`;
    color = props.context?.darkTheme ? '#fda4af' : '#9f1239';
    bg = props.context?.darkTheme ? '#4c0519' : '#ffe4e6';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip label={label} size="small" sx={{ bgcolor: bg, color: color, fontWeight: 'normal', borderRadius: '6px', height: '24px', fontSize: '11px' }} />
    </div>
  );
};

const PayedStatusRenderer = (props) => {
  const dto = props.data.additionalWorkDTO;
  const amount = dto?.additionalWorkApprovedAmount || 0;

  if (!dto || amount === 0) {
    return null;
  }

  let label = "oczekuje...";
  let color = props.context?.darkTheme ? '#c4b5fd' : '#5b21b6';
  let bg = props.context?.darkTheme ? '#2e1065' : '#ede9fe';

  if (dto.isAdditionalWorkPayed) {
    color = props.context?.darkTheme ? '#6ee7b7' : '#065f46';
    bg = props.context?.darkTheme ? '#022c22' : '#d1fae5';

    const displayAmount = props.data.isVATPayer ? amount * 1.23 : amount;
    label = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(displayAmount);
  } else {
    const displayAmount = props.data.isVATPayer ? amount * 1.23 : amount;
    label = `${new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(displayAmount)} (oczekuje)`;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip label={label} size="small" sx={{ bgcolor: bg, color: color, fontWeight: 'normal', borderRadius: '6px', height: '24px' }} />
    </div>
  );
};

const FinalPayedRenderer = (props) => {
  let label = "oczekuje...";
  let color = props.context?.darkTheme ? '#cbd5e1' : '#475569';
  let bg = props.context?.darkTheme ? '#1e293b' : '#f1f5f9';

  if (props.data.payed === true) {
    label = `ZAPŁACONE! ${props.data.payDate}`;
    color = props.context?.darkTheme ? '#6ee7b7' : '#065f46';
    bg = props.context?.darkTheme ? '#022c22' : '#d1fae5';
  } else if (props.data.partiallyPayedAmount !== null) {
    label = `CZĘŚCIOWO ZAPŁ. ${props.data.payDate}`;
    color = props.context?.darkTheme ? '#fcd34d' : '#92400e';
    bg = props.context?.darkTheme ? '#451a03' : '#fef3c7';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip label={label} size="small" sx={{ bgcolor: bg, color: color, fontWeight: 'normal', borderRadius: '6px', height: '24px' }} />
    </div>
  );
};

function GenerateInvoices() {
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [accountingStatements, setAccountingStatements] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    String(parseInt(CommonFunctions.getMonthForSelect(), 10))
  );
  const [selectedYear, setSelectedYear] = useState(
    String(parseInt(CommonFunctions.getYearForSelect(), 10))
  );
  const theme = useTheme();
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const userName = useAuthStore().fullName;
  const contactId = useAuthStore().contactId;
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [openConfirmPaymentDialog, setOpenConfirmPaymentDialog] = useState(false);
  const [accountingSettlement, setAccountingSettlement] = useState([]);
  const [url, setUrl] = useState('');
  const [accountants, setAccountants] = useState([]);
  const [supervisor, setSupervisor] = useState([]);
  const [supervisorName, setSupervisorName] = useState('')
  const [currentDate, setCurrentDate] = useState(CommonFunctions.getCurrentDate())
  const [selectedSupervisor, setSelectedSupervisor] = useState("0");
  const classes = useStyles();
  const isSupervisorAccountant = roles?.includes('SUPERVISOR_ACCOUNTANT');
  const isChiefAccountant = roles?.includes('CHIEF_ACCOUNTANT');
  const canToggleView = isChiefAccountant;
  const [viewMode, setViewMode] = useState(canToggleView ? 'ACCOUNTANT' : null);

  const isViewOnly = viewMode === 'ACCOUNTANT' || (!isChiefAccountant && (roles?.includes('ACCOUNTANT') || isSupervisorAccountant));
  const isViewOnlyExtended = roles?.includes('CHIEF_ACCOUNTANT') && viewMode !== 'ACCOUNTANT';
  const isApproveButtonAllowed = roles?.includes('MANAGER') || roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR');
  const [partiallyPayedAmount, setPartiallyPayedAmount] = useState(false);
  const totalSalaryPayed = tableData.reduce((sum, row) => {
    const salary = row.isVATPayer ? row.salary * 1.23 : row.salary;
    return row.payed ? sum + salary : sum;
  }, 0);

  const totalSalaryUnpayed = tableData.reduce((sum, row) => {
    const salary = row.isVATPayer ? row.salary * 1.23 : row.salary;
    return !row.payed ? sum + salary : sum;
  }, 0);

  const estimatedWaitingAmount = tableData.reduce((sum, row) => {
    const salary = row.previousMonthSalary;
    return !row.statementFinished ? sum + salary : sum;
  }, 0);

  const totalAdditionalWorkAmount = tableData.reduce((sum, row) => {
    const additionalWorkAmount = row.isVATPayer
      ? (row.additionalWorkDTO?.additionalWorkAmount || 0) * 1.23
      : (row.additionalWorkDTO?.additionalWorkAmount || 0);
    return sum + additionalWorkAmount;
  }, 0);
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const values = ['wygenerowano', 'oczekuje...'];

  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "Księgowa/y",
      field: "accountantName",
      sortable: true,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 160,
      tooltipValueGetter: (params) => params.data.accountantCompanyName,
      cellRenderer: NameAvatarRenderer,
      valueGetter: (params) => params.data.accountantName,
    },
    {
      headerName: "Zestawienie",
      field: "statementFinished",
      lockPosition: 'left',
      sortable: true,
      minWidth: 130,
      cellRenderer: StatementFinishedRenderer,
    },
    {
      headerName: "Faktury",
      field: "generatedInvoices",
      lockPosition: 'left',
      minWidth: 140,
      headerClass: 'no-separator',
      editable: !isViewOnlyExtended,
      hide: isViewOnly,
      cellRenderer: GeneratedInvoicesRenderer,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: values,
      },
      onCellValueChanged: function (params) {
        let dataCopy = { ...params.data };
        delete dataCopy.generateInvoiceCheckbox;
        delete dataCopy.payedCheckbox;
        if (params.newValue === 'oczekuje...') {
          if (params.oldValue === 'wygenerowano') {
            alert("Wykonujesz operacje zmiany statusu generowania faktur - pamiętaj, że ponowne generowanie faktur nie uwzględni już zrealizowanych rabatów.")
          }
          dataCopy.generatedInvoices = false;
        } else {
          dataCopy.generatedInvoices = true;
        }
        updateAccountingSettlement(urlAccountingSettlement, params.data.id, dataCopy, params.data.month, selectedSupervisor);
      }
    },
    {
      headerName: "",
      field: "generateInvoiceCheckbox",
      maxWidth: 40,
      minWidth: 40,
      lockPosition: 'left',
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: true,
      hide: isViewOnly || isViewOnlyExtended,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      editable: function (params) {
        return params.data.generatedInvoices === "oczekuje...";
      }
    },
    {
      headerName: "Superwizor",
      field: "statementApproved",
      lockPosition: 'left',
      minWidth: 170,
      cellRenderer: StatementApprovedRenderer,
    },
    {
      headerName: "Zatwierdzona kwota za zestawienie",
      field: "salary",
      lockPosition: 'left',
      minWidth: 160,
      hide: !isViewOnly,
      cellStyle: function (params) {
        if (params.data.statementApproved) {
          return {
            fontWeight: '600',
            color: darkTheme ? '#4ade80' : '#16a34a' // soft green text
          };
        }
        return {};
      },
      valueGetter: function (params) {
        if (params.data.statementApproved) {
          const amount = params.data.isVATPayer ? params.data.salary * 1.23 : params.data.salary;
          const typeLabel = params.data.isVATPayer ? "brutto" : "netto";
          return `${new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount)} ${typeLabel}`;
        }
        return "";
      }
    },
    {
      headerName: "Zatwierdzona kwota za prace dodatkowe",
      field: "additionalWorkApprovedAmount",
      lockPosition: 'left',
      minWidth: 160,
      hide: isViewOnly,
      cellStyle: function (params) {
        const dto = params.data.additionalWorkDTO;
        if (dto && dto.additionalWorkApprovalDate) {
          return {
            fontWeight: '600',
            color: darkTheme ? '#4ade80' : '#16a34a' // soft green text
          };
        }
        return {};
      },
      valueGetter: function (params) {
        const dto = params.data.additionalWorkDTO;
        if (dto && dto.additionalWorkApprovalDate) {
          const isExcludedFromVAT = dto.additionalWorkTypeId === 11;
          let amount = dto.additionalWorkApprovedAmount || 0;
          if (params.data.isVATPayer && !isExcludedFromVAT) {
            amount = amount * 1.23;
          }
          const typeLabel = (params.data.isVATPayer && !isExcludedFromVAT) ? "brutto" : "netto";
          const formattedValue = `${new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount)} ${typeLabel}`;
          const isAccountant = roles?.includes('ACCOUNTANT') || roles?.includes('SUPERVISOR_ACCOUNTANT') || roles?.includes('CHIEF_ACCOUNTANT');
          if (isAccountant && params.data.isCorrectionRequired) {
            return `${formattedValue} (wgraj korektę)`;
          }
          return formattedValue;
        }
        return "";
      },
      cellRenderer: function (params) {
        const value = params.value;
        if (!value) return "";
        if (value.includes(" (wgraj korektę)")) {
          const parts = value.split(" (wgraj korektę)");
          return (
            <span>
              {parts[0]} <span style={{ fontWeight: 'bold', color: '#ef4444' }}>(wgraj korektę)</span>
            </span>
          );
        }
        return value;
      }
    },
    {
      headerName: "Uwagi do prac dodatkowych",
      field: "additionalWorkComment",
      lockPosition: 'left',
      minWidth: 150,
      cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      hide: isViewOnly,
      tooltipValueGetter: (params) => params.data.additionalWorkDTO?.additionalWorkComment || "",
      valueGetter: function (params) {
        return params.data.additionalWorkDTO?.additionalWorkComment || "";
      }
    },
    {
      headerName: "Do zapłaty brutto",
      field: "salary",
      lockPosition: 'left',
      minWidth: 140,
      editable: false,
      hide: isViewOnly,
      valueFormatter: function (params) {
        if (params.data.statementFinished && !params.data.payed) {
          let salary = params.value;
          let additionalWorkAmount = params.data.additionalWorkDTO?.additionalWorkAmount || 0
          // Dodanie VAT, jeśli użytkownik jest płatnikiem VAT
          if (params.data.isVATPayer) {
            salary = (salary + additionalWorkAmount) * 1.23;
          }
          else {
            salary = (salary + additionalWorkAmount);
          }
          // Formatuj wartość końcową
          return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(salary);
        } else {
          return "";
        }
      },
      cellStyle: function (params) {
        const hasValue = params.data.statementFinished && !params.data.payed;
        if (hasValue) {
          return {
            fontWeight: '600',
            color: darkTheme ? '#38bdf8' : '#0284c7' // Soft light blue for emphasis without background
          };
        }
        return {};
      },
    },
    {
      headerName: "Zapłacone prace dodatkowe",
      field: "additionalWorkApprovedAmount",
      lockPosition: 'left',
      minWidth: 160,
      editable: false,
      hide: isViewOnly,
      headerClass: (isViewOnly || isViewOnlyExtended) ? '' : 'no-separator',
      tooltipValueGetter: (params) => {
        const dto = params.data.additionalWorkDTO;
        if (!dto || !dto.additionalWorkApprovedAmount || dto.additionalWorkApprovedAmount === 0) return "";
        if (dto.isAdditionalWorkApproved && !dto.isAdditionalWorkPayed) {
          return `Zatwierdzone przez ${dto.additionalWorkApproverName || ""} ${dto.additionalWorkApprovalDate || ""}`;
        }
        if (dto.isAdditionalWorkPayed) {
          return `Zapłacone przez ${dto.additionalWorkPayerName || ""} ${dto.additionalWorkPayDate || ""}`;
        }
        return "";
      },
      cellRenderer: PayedStatusRenderer,
    },
    {
      headerName: "",
      field: "paidAdditionalWorkCheckbox",
      lockPosition: 'left',
      minWidth: 40,
      maxWidth: 40,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      hide: isViewOnly || isViewOnlyExtended,
      cellRendererParams: function (params) {
        const dto = params.data.additionalWorkDTO;
        const amount = dto?.additionalWorkApprovedAmount || 0;
        const isDisabled = !amount || amount === 0 || dto?.isAdditionalWorkPayed;
        return { disabled: isDisabled };
      },
      editable: function (params) {
        const dto = params.data.additionalWorkDTO;
        const amount = dto?.additionalWorkApprovedAmount || 0;
        return dto && dto.isAdditionalWorkApproved && !dto.isAdditionalWorkPayed && amount > 0;
      },
      cellStyle: function (params) {
        return { display: 'flex', justifyContent: 'center', alignItems: 'center' };
      },
      valueGetter: function (params) {
        if (params.data.additionalWorkDTO?.isAdditionalWorkPayed) {
          return true;
        } else {
          return false;
        }
      }
    },
    {
      headerName: "Zapłacone brutto",
      field: "salary",
      lockPosition: 'left',
      minWidth: 150,
      editable: false,
      hide: isViewOnly,
      headerClass: (isViewOnly || isViewOnlyExtended) ? 'no-separator' : '',
      cellStyle: function (params) {
        const hasValue = params.data.payed;
        if (!hasValue) {
          return {
            padding: 0
          };
        }
        return {
          padding: 0 // usuwa wewnętrzny padding, by nie zaburzał wysokości
        };
      },
      cellRenderer: function (params) {
        const { fileMetadataDTOS = [], payed, additionalWorkDTO, isVATPayer, isDownloaded } = params.data;

        // Obliczanie wartości z warunkami
        let value = params.value || 0;
        if (payed) {
          const additionalWorkApprovedAmount = additionalWorkDTO?.additionalWorkApprovedAmount || 0;
          value += additionalWorkApprovedAmount;
          if (isVATPayer && value > 0) {
            value *= 1.23;
          }
          value = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value);
        } else {
          value = "";
        }

        // Funkcja pomocnicza do wyszukiwania plików
        const findFileByType = (fileType) =>
          fileMetadataDTOS.find((file) => file.fileType === fileType);

        // Pliki o typach 11 i 21
        const fileType11 = findFileByType(11);
        const fileType21 = findFileByType(21);

        // Styl dla ikonek
        const iconStyle = {
          cursor: 'pointer',
          marginLeft: '5px',
          color: '#0068b3',
          fontSize: '18px'
        };

        // Funkcja wywołania pobrania pliku
        const handleFileDownload = (fileMetadata) => {
          CommonFunctions.downloadFile(
            fileMetadata.id,
            fileMetadata.fileType,
            params.data.accountantName,
            params.data.month,
            params.data.year,
            token
          );
        };

        const containerStyle = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          color: darkTheme ? '#4fc3f7' : '#0068b3',
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          padding: '0 4px',
          opacity: isDownloaded && isViewOnlyExtended ? 0.5 : 1,
          pointerEvents: isDownloaded && isViewOnlyExtended ? 'none' : 'auto'
        };

        return (
          <div style={containerStyle}>
            <span>{value}</span>

            {/* Ikonka PDF dla fileType 11 */}
            {(payed && fileType11) && (
              <i
                className="fas fa-file-pdf"
                style={iconStyle}
                title="Pobierz plik faktury za zestawienie"
                onClick={() => handleFileDownload(fileType11)}
              />
            )}

            {(payed && fileType21) && (
              <i
                className="fas fa-file-pdf"
                style={iconStyle}
                title="Pobierz plik faktury za prace dodatkowe"
                onClick={() => handleFileDownload(fileType21)}
              />
            )}
          </div>
        );
      }
    },
    {
      headerName: "",
      field: "isDownloaded",
      lockPosition: 'left',
      minWidth: 50,
      maxWidth: 50,
      editable: true,
      hide: !isViewOnlyExtended,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      cellStyle: function (params) {
        return {
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        };
      },

      valueGetter: function (params) {
        return params.data.isDownloaded === true;
      },

      cellRendererParams: function (params) {
        // checkbox nieaktywny jeśli zapłacone brutto (salary) nie ma sensownej wartości
        const { payed, additionalWorkDTO, isVATPayer } = params.data;

        let brutto = params.data.salary || 0;
        if (payed) {
          brutto += additionalWorkDTO?.additionalWorkApprovedAmount || 0;
          if (isVATPayer && brutto > 0) {
            brutto *= 1.23;
          }
        } else {
          brutto = 0;
        }

        const isDisabled = !brutto || brutto === 0;

        return { disabled: isDisabled };
      },

      valueSetter: function (params) {
        const newValue = params.newValue;
        let generatedInvoices;

        if (params.data.isDownloaded !== newValue) {
          params.data.isDownloaded = newValue;

          if (params.data.generatedInvoices === 'oczekuje...') {
            generatedInvoices = false;
          } else {
            generatedInvoices = true;
          }

          // Pomijamy isGeneratedInvoices z params.data
          const { isGeneratedInvoices, ...restData } = params.data;

          const dataToSend = {
            ...restData,
            isDownloaded: newValue,
            generatedInvoices: generatedInvoices
          };

          const accountingSettlementId = params.data.id;

          updateAccountingSettlement(
            urlAccountingSettlement,
            accountingSettlementId,
            dataToSend,
            params.data.month,
            false
          );

          params.api.refreshCells({ rowNodes: [params.node], force: true });

          return true;
        }

        return false;
      }
    },
    {
      headerName: "Wypłata",
      field: "payed",
      lockPosition: 'left',
      minWidth: 180,
      editable: false,
      headerClass: 'no-separator',
      tooltipValueGetter: (params) => {
        if (params.data.partiallyPayedAmount !== null) {
          return `Przelano ${params.data.partiallyPayedAmount} zł w dniu ${params.data.payDate}`;
        }
        return null;
      },
      cellRenderer: FinalPayedRenderer,
    },
    {
      headerName: "",
      field: "payedCheckbox",
      lockPosition: 'left',
      minWidth: 50,
      maxWidth: 50,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      headerClass: 'no-separator',
      hide: isViewOnly || isViewOnlyExtended,
      cellRendererParams: function (params) {
        return { disabled: params.data.payed };
      },
      editable: function (params) {
        return !params.data.payed;
      },
      valueGetter: function (params) {
        if (params.data.payed) {
          return true;
        } else {
          return false;
        }
      },
      cellStyle: function (params) {
        return { display: 'flex', justifyContent: 'center', alignItems: 'center' };
      },
    },
  ], [darkTheme, isViewOnly, isViewOnlyExtended, roles, viewMode]);

  useEffect(() => {
    fetchCurrentData(selectedMonth, selectedYear, selectedSupervisor, viewMode);
    getSupervisors();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isGeneratingInvoices) {
        e.preventDefault();
        e.returnValue = 'Trwa generowanie faktur. Przerwanie może spowodować brak kompletu dokumentów. Czy na pewno chcesz wyjść?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isGeneratingInvoices]);

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const defaultColDef = {
    resizable: true,
    editable: false,
    filter: false,
    flex: 1,
    tooltipComponent: CustomTooltip,
  }

  const handleMonthChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedMonth(selectedValue);
    fetchCurrentData(selectedValue, selectedYear, selectedSupervisor, viewMode);
  };

  const handleYearChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedYear(selectedValue);
    fetchCurrentData(selectedMonth, selectedValue, selectedSupervisor, viewMode);
  };

  const handleSupervisorChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedSupervisor(selectedValue);
    fetchCurrentData(selectedMonth, selectedYear, selectedValue, viewMode);
  };


  const handleOpenConfirmPaymentDialog = () => {
    let length = getSelectedPayedRecords().length;
    if (length > 0 && length < 2) {
      accountingSettlement.forEach((item) => {
        if (getSelectedPayedRecords().includes(item.id)) {
          setPartiallyPayedAmount(item.partiallyPayedAmount);
        }
      });
      setOpenConfirmPaymentDialog(true);
    }
    else if (length >= 2) {
      setOpenConfirmPaymentDialog(true);
    }
    else {
      alert("Nie zaznaczyłeś żadnego elementu w kolumnie \"Wypłata\"");
    }
  }

  const handleConfirmPayments = (amount = null) => {
    accountingSettlement.forEach((item) => {
      if (getSelectedPayedRecords().includes(item.id)) {
        item.payDate = CommonFunctions.convertDateToJavaFormat(currentDate);
        if (amount !== null) {
          item.payed = false;
          item.partiallyPayedAmount = amount;
        } else {
          item.payed = true;
          item.partiallyPayedAmount = null;
        }
        updateAccountingSettlement(urlAccountingSettlement, item.id, item, item.month);
        logActivity(logActionType.CONFIRM_PAYMENT + item.accountantName + " do zestawieni za miesiąc: " + item.month + "/" + item.year, userName, token);
      }
    });
    resetCheckbox("payedCheckbox");
    setOpenConfirmPaymentDialog(false);
  };

  const handleCloseConfirmPaymentDialog = () => {
    setOpenConfirmPaymentDialog(false);
    setPartiallyPayedAmount(null);
    resetCheckbox("payedCheckbox");
  };

  const mapAccountingSettlement = (item) => {
    return {
      ...item,
      generateInvoiceCheckbox: false,
      payedCheckbox: false,
      paidAdditionalWorkCheckbox: false,
      generatedInvoices: item.generatedInvoices ? 'wygenerowano' : 'oczekuje...',
    };
  };

  const getSelectedPayedRecords = () => {
    if (tableData && tableData.length > 0) {
      const supervisorName = tableData[0]?.supervisorName;

      const selectedData = tableData
        .filter((data) => data.payedCheckbox === true);

      const selectedAccountants = selectedData
        .map((data) => data.accountantName);

      const selectedAccountingSettlementsIds = selectedData
        .map((data) => data.id)

      setAccountants(selectedAccountants);
      setSupervisorName(supervisorName);
      return selectedAccountingSettlementsIds;
    }
  };

  //Generate Invoice Dialog

  const handleOpenCreateInvoices = async () => {
    if (getSelectedAccountantIds().length > 0) {
      try {
        await fetchAccountingStatements(selectedMonth, selectedYear, getSelectedAccountantIds());
        setOpenInvoiceDialog(true);
      } catch (error) {
        alert("Błąd podczas pobierania danych:");
      }
    } else {
      alert("Nie zaznaczyłeś żadnego elementu w kolumnie \"Faktury\"");
    }
  };


  const getSelectedApproveAdditionalWorkRecords = () => {
    if (tableData && tableData.length > 0) {
      const selectedData = tableData
        .filter((data) => data.paidAdditionalWorkCheckbox === true);

      const selectedAccountants = selectedData
        .map((data) => data.accountantName);

      const selectedAccountingSettlementsIds = selectedData
        .map((data) => data.id)

      setAccountants(selectedAccountants);
      return selectedAccountingSettlementsIds;
    }
  };

  const handleAdditionalWorkPaid = () => {
    accountingSettlement.forEach((item) => {
      if (getSelectedApproveAdditionalWorkRecords().includes(item.id)) {
        item.additionalWorkDTO.isAdditionalWorkPayed = true;
        item.additionalWorkDTO.additionalWorkPayDate = CommonFunctions.convertDateToJavaFormat(currentDate);
        item.additionalWorkDTO.additionalWorkPaidBy = contactId;
        updateAccountingSettlement(urlAccountingSettlement, item.id, item, item.month);
        logActivity(logActionType.ADDITIONAL_WORK_PAID + item.accountantName + " do zestawieni za miesiąc: " + item.month + "/" + item.year, userName, token);
      }
    });
    resetCheckbox("paidAdditionalWorkCheckbox");
    setOpenConfirmPaymentDialog(false);
  };

  const handleOpenApproveAdditionalWork = async () => {
    if (getSelectedApproveAdditionalWorkRecords().length > 0) {
      handleAdditionalWorkPaid()
    }
    else {
      alert("Nie zaznaczyłeś żadnego elementu w kolumnie \"Zapłacone prace dodatkowe\"");
    }
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const retryCreateInvoice = async (rowData, month, year, retries = 2, retryDelay = 500) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const data = await createInvoice(rowData, token);
        return data;
      } catch (error) {
        if (attempt === retries) {
          throw error;
        } else {
          logActivity(`Ponowiono próbę generowania faktury za ${month}/${year} dla: ${rowData.clientName} ze względu na błąd: ${error.message}`, userName, token);
          await delay(retryDelay); // Add delay before the next retry
        }
      }
    }
  };

  const getFakturaWordForm = (count) => {
    if (count === 1) return "faktura";
    if (count === 0) return "faktur";
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 10 && lastTwoDigits <= 21) return "faktur";
    if (lastDigit > 1 && lastDigit < 5) return "faktury";
    return "faktur";
  };

  const handleCreateInvoices = async () => {
    try {
      setIsGeneratingInvoices(true); // START
      const errorMessages = [];
      const successfulStatements = [];
      const accountants = [];
      let month = CommonFunctions.getMonthForSelect(parseInt(selectedYear, 10), parseInt(selectedMonth, 10), true);
      let year = CommonFunctions.getYearForSelect(selectedMonth, selectedYear, true);

      accountingSettlement.forEach((item) => {
        if (getSelectedAccountantIds().includes(item.accountantId)) {
          accountants.push(" " + item.accountantName);
          item.generatedInvoices = true;
          updateAccountingSettlement(urlAccountingSettlement, item.id, item, item.month);
          logActivity(
            logActionType.GENERATE_INVOICES_START + month + "/" + year + " dla księgowego: " + item.accountantName,
            userName,
            token
          );
        }
      });

      for (let index = 0; index < accountingStatements.length; index++) {
        const rowData = accountingStatements[index];
        await delay(index * 500);

        try {
          await retryCreateInvoice(rowData, month, year);
          successfulStatements.push(rowData);
        } catch (error) {
          errorMessages.push(`- ${rowData.clientName}`);
          logActivity(
            `Nie udało się wygenerować faktury za ${month}/${year} dla: ${rowData.clientName}. Błąd: ${error.message}`,
            userName,
            token
          );
        }
      }

      // Zbierz wszystkie ID z discountDTOs
      const allDiscountIds = successfulStatements
        .flatMap(statement => Array.isArray(statement.discountDTOs) ? statement.discountDTOs : [])
        .filter(discount => discount?.id)
        .map(discount => discount.id);

      // Wysyłka zbiorczego żądania
      if (allDiscountIds.length > 0) {
        try {
          await fetch(`${urlAdditionalCosts}/discount/executed`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(allDiscountIds) // 👈 NIE obiekt, tylko sama lista
          });
        } catch (err) {
          console.error("Błąd podczas oznaczania rabatów jako wykonane:", err);
          // Możesz tu dodać logActivity, jeśli ważne
        }
      }

      if (errorMessages.length > 0) {
        alert(
          `Wygenerowano ${accountingStatements.length - errorMessages.length} ${getFakturaWordForm(accountingStatements.length - errorMessages.length)} do zestawienia${accountants}. \n\n` +
          'Wystąpiły problemy podczas generowania faktur dla firm:\n\n' +
          errorMessages.join('\n') + '\n\n Szczegóły błędów zarejestrowano w historii aktywności.'
        );
      } else {
        alert(
          `Wygenerowano ${accountingStatements.length} ${getFakturaWordForm(accountingStatements.length)} do zestawienia${accountants}.`
        );
      }

      accountingSettlement.forEach((item) => {
        if (getSelectedAccountantIds().includes(item.accountantId)) {
          item.generatedInvoices = true;
          updateAccountingSettlement(urlAccountingSettlement, item.id, item, item.month);
          logActivity(
            logActionType.GENERATE_INVOICES_END + month + "/" + selectedYear + " dla księgowego: " + item.accountantName,
            userName,
            token
          );
        }
      });

      resetCheckbox("generateInvoiceCheckbox");
      setOpenInvoiceDialog(false);
    } finally {
      setIsGeneratingInvoices(false); // END (również przy błędach)
    }
  };



  const handleCloseCreateInvoices = () => {
    setOpenInvoiceDialog(false);
    resetCheckbox("generateInvoiceCheckbox");
  };

  const resetCheckbox = (checkboxName) => {
    const updatedTableData = tableData.map(data => ({
      ...data,
      [checkboxName]: false, // Użycie nazwy checkboxa jako klucza
    }));

    setTableData(updatedTableData);
  };

  const getSelectedAccountantIds = () => {
    if (tableData && tableData.length > 0) {
      const selectedAccountantIds = tableData
        .filter((data) => data.generateInvoiceCheckbox === true)
        .map((data) => data.accountantId);
      return selectedAccountantIds;
    }
    return null
  };

  const updateAccountingSettlement = (url, accountingSettlementId, dataToSend, selectedMonthLocal, shouldReload = true) => {
    selectedMonthLocal = String(parseInt(selectedMonthLocal, 10))
    fetch(url + `/${accountingSettlementId}`, {
      method: "PUT",
      body: JSON.stringify(dataToSend),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }).then(resp => resp.json())
      .then(resp => {
        if (shouldReload) {
          fetchCurrentData(selectedMonthLocal, selectedYear, selectedSupervisor, viewMode);
        }
      })
      .catch(error => {
        console.error('Błąd podczas wysyłania rekordu accountingSettlement:', error);
      });
  }

  const fetchAccountingSettlement = (month, year, supervisor, mode = viewMode) => {
    const selectedMonth = MyCollections.getMonthCollection().find(
      (item) => item.id === month
    );

    const monthParam = selectedMonth ? selectedMonth.id : '';

    const url = urlAccountingSettlement + `/all?supervisorId=${supervisor}&month=${monthParam}&year=${year}`;
    setUrl(url);

    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }
    )
      .then(response => response.json())
      .then(data => {
        let finalData = data;
        if (isSupervisorAccountant && mode === 'SUPERVISOR') {
          finalData = data.filter(item => Number(item.accountantId) !== Number(contactId));
        }
        setAccountingSettlement(finalData);
        const rowData = finalData.map(mapAccountingSettlement);
        setTableData(rowData);
      })
      .catch(error => console.error(error));
  };

  const fetchAccountingSettlementForAccountant = (month, year, accountantId) => {
    const selectedMonth = MyCollections.getMonthCollection().find(
      (item) => item.id === month
    );

    const monthParam = selectedMonth ? selectedMonth.id : '';

    const url = urlAccountingSettlement + `/?accountantId=${accountantId}&month=${monthParam}&year=${year}`;
    setUrl(url);

    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(response => response.json())
      .then(data => {
        // Assuming 'data' is an object and we need to convert it into a list
        const dataList = Array.isArray(data) ? data : [data];
        setTableData(dataList);
      })
      .catch(error => console.error(error));
  };

  const fetchCurrentData = (month = selectedMonth, year = selectedYear, supervisor = selectedSupervisor, mode = viewMode) => {
    const activeIsViewOnly = mode === 'ACCOUNTANT' || (!isChiefAccountant && (roles?.includes('ACCOUNTANT') || isSupervisorAccountant));
    const activeIsViewOnlyExtended = roles?.includes('CHIEF_ACCOUNTANT') && mode !== 'ACCOUNTANT';
    if (!activeIsViewOnly || activeIsViewOnlyExtended) {
      fetchAccountingSettlement(month, year, supervisor, mode);
    } else {
      fetchAccountingSettlementForAccountant(month, year, contactId);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    fetchCurrentData(selectedMonth, selectedYear, selectedSupervisor, mode);
  };

  const fetchAccountingStatements = (month, year, accountantIds) => {
    return new Promise((resolve, reject) => {
      const selectedMonth = MyCollections.getMonthCollection().find(
        (item) => item.id === month
      );

      const monthParam = selectedMonth ? selectedMonth.id : '';

      const url = urlAccountingStatements + "/allChecked";

      const requestData = {
        month: monthParam,
        year: year,
        accountantIds: accountantIds,
      };

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData),
      })
        .then(response => response.json())
        .then(data => {
          setAccountingStatements(data);
          resolve(); // Rozwiąż promis po zakończeniu pobierania danych
        })
        .catch(error => {
          console.error(error);
          reject(error); // Odrzuć promis w przypadku błędu
        });
    });
  };

  const getSupervisors = () => {
    fetch(urlSupervisors + "/allForSelect", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          return {
            id: item.id,
            title: item.surname + ' ' + item.name,
          };
        });

        // Dodaj statyczny element na początku listy
        const updatedData = [{ id: 0, title: "Wszyscy Superwizorzy" }, ...rowData];

        setSupervisor(updatedData);
      })
      .catch(error => {
        console.error('Błąd pobierania danych:', error);
      });
  };
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value);
  };

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        {canToggleView && (
          <Box display="flex" gap="10px" sx={{ px: 2, pt: 2, pb: 1 }}>
            <Controls.PremiumButton
              variant={viewMode === 'ACCOUNTANT' ? 'blue' : 'glass'}
              size="small"
              onClick={() => handleViewModeChange('ACCOUNTANT')}
              label="Widok Księgowego"
              icon="fas fa-user-tie"
            />
            {isChiefAccountant && (
              <Controls.PremiumButton
                variant={viewMode === 'CHIEF' ? 'blue' : 'glass'}
                size="small"
                onClick={() => handleViewModeChange('CHIEF')}
                label="Widok Głównego Księgowego"
                icon="fas fa-user-shield"
              />
            )}

          </Box>
        )}
        <Box sx={{ m: '15px' }} />
        {!isViewOnly ? (
          <Box display="flex" flexDirection="column" gap={1.5} sx={{ mb: 3, px: 2, pt: 2 }}>
            {/* Górny wiersz: Filtry i Akcje */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap="12px"
            >
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                <Box minWidth={220}>
                  <Controls.MySelect
                    name="monthId"
                    label="Rozliczenie zestawienia za miesiąc: "
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    options={MyCollections.getMonthCollectionForGenerateInvoices(parseInt(CommonFunctions.getMonthForSelect(selectedYear), 10))}
                  />
                </Box>
                <Box minWidth={110}>
                  <Controls.MySelect
                    name="yearId"
                    label="Rok: "
                    value={selectedYear}
                    onChange={handleYearChange}
                    options={MyCollections.getYearCollection(2023, CommonFunctions.getYearForSelect())}
                  />
                </Box>
                <Box minWidth={180}>
                  <Controls.MySelect
                    name="supervisorId"
                    label="Superwizor: "
                    value={selectedSupervisor}
                    onChange={handleSupervisorChange}
                    options={supervisor}
                  />
                </Box>
              </Box>

              {/* Prawa strona: Przyciski akcji */}
              <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px" sx={{ pt: 0.5 }}>
                {!isViewOnlyExtended && (
                  <>
                    <Controls.PremiumButton
                      variant="blue"
                      size="small"
                      label="Akcje"
                      icon="fas fa-caret-down"
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                    />
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 220,
                          borderRadius: '8px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {!(isViewOnly || isViewOnlyExtended) && (
                        <MenuItem onClick={() => { setAnchorEl(null); handleOpenApproveAdditionalWork(); }}>
                          <ListItemIcon><i className="fas fa-check-circle" style={{ color: '#3b82f6', fontSize: '1.2rem' }}></i></ListItemIcon>
                          <ListItemText primary="Zapłacono za prace dodatkowe" />
                        </MenuItem>
                      )}
                      {[
                        <MenuItem key="generate" onClick={() => { setAnchorEl(null); handleOpenCreateInvoices(); }} disabled={isGeneratingInvoices}>
                          <ListItemIcon><i className={isGeneratingInvoices ? "fas fa-spinner fa-spin" : "fas fa-file-invoice-dollar"} style={{ color: '#10b981', fontSize: '1.2rem' }}></i></ListItemIcon>
                          <ListItemText primary={isGeneratingInvoices ? 'Generowanie...' : 'Generuj faktury'} />
                        </MenuItem>,
                        <MenuItem key="confirm" onClick={() => { setAnchorEl(null); handleOpenConfirmPaymentDialog(); }}>
                          <ListItemIcon><i className="fas fa-money-bill-wave" style={{ color: '#6366f1', fontSize: '1.2rem' }}></i></ListItemIcon>
                          <ListItemText primary="Potwierdź realizację przelewu" />
                        </MenuItem>
                      ]}
                    </Menu>
                  </>
                )}
              </Box>
            </Box>

            {/* Dolny wiersz: Statystyki */}
            <div className="stat-badges-row" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div className="stat-badge stat-badge--green">
                <i className="fas fa-check-circle"></i>
                Zapłacone: <strong>{formatCurrency(totalSalaryPayed)}</strong>
              </div>
              <div className="stat-badge stat-badge--amber">
                <i className="fas fa-exclamation-circle"></i>
                Do zapłaty: <strong>{formatCurrency(totalSalaryUnpayed)}</strong>
              </div>
              <div className="stat-badge stat-badge--blue">
                <i className="fas fa-plus-circle"></i>
                W tym PD: <strong>{formatCurrency(totalAdditionalWorkAmount)}</strong>
              </div>
              <div className="stat-badge stat-badge--purple">
                <i className="fas fa-hourglass-half"></i>
                Estymowana kwota oczekujących: <strong>{formatCurrency(estimatedWaitingAmount)}</strong>
              </div>
            </div>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3, px: 2, pt: 2 }}>
            <Box minWidth={280}>
              <Controls.MySelect
                name="monthId"
                label="Rozliczenie zestawienia za miesiąc: "
                value={selectedMonth}
                onChange={handleMonthChange}
                options={MyCollections.getMonthCollectionForGenerateInvoices(parseInt(CommonFunctions.getMonthForSelect(selectedYear), 10))}
              />
            </Box>
            <Box minWidth={120}>
              <Controls.MySelect
                name="yearId"
                label="Rok: "
                value={selectedYear}
                onChange={handleYearChange}
                options={MyCollections.getYearCollection(2023, CommonFunctions.getYearForSelect())}
              />
            </Box>
          </Box>
        )}
        <Box sx={{ m: '20px' }} />
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12}>
            <div className="App">
              <div className="tableData">
                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    rowData={tableData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={200}
                    suppressBrowserResizeObserver={true}
                    animateRows={true}
                    rowSelection={false}
                    suppressSizeToFit={true}
                    enableBrowserTooltips={false}
                    localeText={MyCollections.localeTextPL}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <GenerateInvoiceDialog open={openInvoiceDialog} handleClose={handleCloseCreateInvoices} handleCreateInvoices={handleCreateInvoices} month={CommonFunctions.getMonthForSelect(parseInt(selectedYear, 10), parseInt(selectedMonth, 10), true)} year={CommonFunctions.getYearForSelect(selectedMonth, selectedYear, true)} />
        <ConfirmPaymentDialog open={openConfirmPaymentDialog} handleClose={handleCloseConfirmPaymentDialog} handleConfirmPayments={handleConfirmPayments} month={selectedMonth} year={CommonFunctions.getCurrentYear()} accountants={accountants} signature={userName} date={currentDate} partiallyPayedAmount={partiallyPayedAmount} darkTheme={darkTheme} />
      </Paper>
    </>
  );
}

export default GenerateInvoices;